import { Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { useState } from "react";

import { DataTable, DateText, EmptyState, ErrorState, LoadingState, PageHeader, StatusChip, useListParams } from "../components";
import { formatRelative } from "../i18n/format";
import help from "./HELP.md?raw";
import { useApprovals, useDecide, useInbox, type Approval } from "./useData";

function DecideDialog({ item, action, onClose }: { item: Approval; action: "approve" | "reject"; onClose: () => void }) {
  const decide = useDecide();
  const [note, setNote] = useState("");
  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{action === "approve" ? "Approve" : "Reject"} {item.title}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {decide.isError && <ErrorState message={decide.error.message} />}
        <TextField label="Note (optional)" multiline minRows={2} value={note} onChange={(e) => setNote(e.target.value)} sx={{ mt: 1 }} autoFocus />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color={action === "approve" ? "primary" : "error"} disabled={decide.isPending}
          onClick={() => decide.mutate({ id: item.id, action, note }, { onSuccess: onClose })}>{action === "approve" ? "Approve" : "Reject"}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function ApprovalInbox() {
  const [tab, setTab] = useState<"inbox" | "all">("inbox");
  const inbox = useInbox();
  const [params, setParams] = useListParams({ ordering: "-created_at" });
  const all = useApprovals(params);
  const decide = useDecide();
  const [dialog, setDialog] = useState<{ item: Approval; action: "approve" | "reject" } | null>(null);
  const columns: GridColDef<Approval>[] = [
    { field: "title", headerName: "Request", flex: 1 },
    { field: "key", headerName: "Type", width: 170 },
    { field: "status", headerName: "Status", width: 110, renderCell: (p) => <StatusChip status={p.value as string} /> },
    { field: "requested_by_email", headerName: "Requested by", width: 200 },
    { field: "policy_name", headerName: "Policy", width: 160 },
    { field: "created_at", headerName: "Requested", width: 140, renderCell: (p) => <DateText value={p.value as string} withTime /> },
    { field: "decided_at", headerName: "Decided", width: 140, renderCell: (p) => <DateText value={p.value as string} withTime /> },
  ];
  return (
    <>
      <PageHeader help={help} title="Approvals" subtitle="Decisions waiting for you and the history of every request" crumbs={[{ label: "Approvals" }]} />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}><Tab value="inbox" label={`My inbox${inbox.data ? ` (${inbox.data.length})` : ""}`} /><Tab value="all" label="All requests" /></Tabs>
      {tab === "inbox" ? (
        <>
          {inbox.isLoading && <LoadingState />}
          {inbox.data?.length === 0 && <EmptyState title="Nothing to approve" description="Requests routed to you will appear here." />}
          <Stack spacing={1.5}>
            {inbox.data?.map((item) => (
              <Card key={item.id}><CardContent>
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
                  <div>
                    <Stack direction="row" spacing={1} alignItems="center"><Typography variant="h6">{item.title}</Typography><Chip size="small" label={item.key} /></Stack>
                    <Typography variant="body2" color="text.secondary">Requested by {item.requested_by_email} {formatRelative(item.created_at)}{item.policy_name ? ` · policy ${item.policy_name}` : ""}{item.due_at ? ` · due ${formatRelative(item.due_at)}` : ""}</Typography>
                    {Object.keys(item.context).length > 0 && (
                      <Typography variant="body2" sx={{ mt: 1 }}>{Object.entries(item.context).filter(([k]) => !k.startsWith("_")).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ")}</Typography>
                    )}
                    {item.decisions.length > 0 && <Typography variant="caption" color="text.secondary">{item.decisions.map((d) => `${d.approver_email} ${d.approved ? "approved" : "rejected"}${d.note ? ` (${d.note})` : ""}`).join("; ")}</Typography>}
                  </div>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Button variant="outlined" color="error" onClick={() => setDialog({ item, action: "reject" })}>Reject</Button>
                    <Button variant="contained" onClick={() => setDialog({ item, action: "approve" })}>Approve</Button>
                  </Stack>
                </Stack>
              </CardContent></Card>
            ))}
          </Stack>
        </>
      ) : (
        <DataTable viewKey="workflow.approvals" columns={columns} data={all.data} isLoading={all.isLoading} params={params} onParamsChange={setParams}
          onRowClick={(row) => { if (row.status === "pending" && !row.can_decide) { if (confirm(`Cancel ${row.title}?`)) decide.mutate({ id: row.id, action: "cancel" }); } }} />
      )}
      {dialog && <DecideDialog item={dialog.item} action={dialog.action} onClose={() => setDialog(null)} />}
    </>
  );
}
